import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertBox, Breadcrumb, Button, ContentCard, SectionHeader } from "@zorni/components-lib";

import BookingSteps from "../../components/booking/bookingSteps/BookingSteps";
import { currentLocale } from "../../../common/i18n/locale";
import styles from "./Payments.module.css";

/**
 * Étape paiement — SIMULATION.
 *
 * Aucun prestataire de paiement n'est intégré. Cette page ne demande, ne
 * transmet et ne conserve aucune donnée bancaire : elle joue le déroulé
 * (traitement, succès ou refus) pour que le tunnel puisse être présenté de
 * bout en bout jusqu'à la confirmation. Le choix du résultat est explicite,
 * pour que les deux chemins soient montrables.
 *
 * À remplacer par le vrai règlement, qui devra aussi créer la réservation :
 * aucune mutation n'est appelée aujourd'hui, rien n'est persisté.
 */

const DELAI_TRAITEMENT = 1600;

export function Payments() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tripPublicationId, tripDate } = useParams();

  const [issue, setIssue] = useState("accepted");
  const [status, setStatus] = useState("idle"); // idle | processing | refused

  const booking = location.state?.bookingPreview ?? null;

  const travelers = useMemo(() => {
    if (Array.isArray(location.state?.travelers)) return location.state.travelers;

    try {
      const raw = sessionStorage.getItem(`preview-booking:${tripPublicationId}:${tripDate}`);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed?.travelers) ? parsed.travelers : [];
    } catch {
      return [];
    }
  }, [location.state, tripPublicationId, tripDate]);

  const total = booking?.total;
  const amountLabel =
    total?.value != null
      ? `${Number(total.value).toLocaleString(currentLocale())} ${total.currency ?? ""}`.trim()
      : "";

  const handlePay = () => {
    setStatus("processing");

    window.setTimeout(() => {
      if (issue === "refused") {
        setStatus("refused");
        return;
      }

      navigate(`/trips/${tripPublicationId}/${tripDate}/booking/confirmation`, {
        state: { travelers, bookingPreview: booking, simulated: true },
      });
    }, DELAI_TRAITEMENT);
  };

  const isProcessing = status === "processing";

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Breadcrumb
          className={styles.breadcrumb}
          ariaLabel={t("trips.results.breadcrumb", "Fil d'Ariane")}
          linkComponent={Link}
          items={[
            { label: t("trips.results.home", "Accueil"), to: "/" },
            {
              label: t("TripDetails", "Détail du voyage"),
              to: `/trips/${tripPublicationId}/${tripDate}`,
            },
            { label: t("booking.steps.payment", "Paiement") },
          ]}
        />

        <SectionHeader
          size="page"
          className={styles.header}
          kicker={t("booking.kicker", "Réservation")}
          title={t("booking.payment.title", "Paiement")}
          subtitle={t(
            "booking.payment.subtitle",
            "Dernière étape avant la confirmation de votre dossier."
          )}
        />

        <BookingSteps current={3} />

        <div className={styles.notice}>
          <AlertBox
            type="warning"
            title={t("booking.payment.demoTitle", "Mode démonstration")}
            message={t(
              "booking.payment.demoNotice",
              "La solution de règlement n'est pas encore intégrée. Aucun paiement n'est effectué, aucune donnée bancaire n'est demandée, et aucune réservation n'est enregistrée."
            )}
          />
        </div>

        <div className={styles.grid}>
          <ContentCard
            title={t("booking.payment.method", "Moyen de paiement")}
            titleId="payment-method"
            className={styles.card}
          >
            {/* Carte factice, non saisissable : rien n'est collecté. */}
            <div className={styles.fakeCard} aria-hidden="true">
              <span className={styles.fakeCardLabel}>
                {t("booking.payment.demoCard", "Carte de démonstration")}
              </span>
              <span className={styles.fakeCardNumber}>•••• •••• •••• 4242</span>
              <span className={styles.fakeCardFoot}>
                <span>{t("booking.payment.holder", "TITULAIRE DÉMO")}</span>
                <span>04 / 30</span>
              </span>
            </div>

            <fieldset className={styles.issue} disabled={isProcessing}>
              <legend className={styles.issueLegend}>
                {t("booking.payment.issueLegend", "Résultat à simuler")}
              </legend>

              <label className={styles.issueOption}>
                <input
                  type="radio"
                  name="payment-issue"
                  value="accepted"
                  checked={issue === "accepted"}
                  onChange={() => {
                    setIssue("accepted");
                    setStatus("idle");
                  }}
                />
                {t("booking.payment.accepted", "Paiement accepté")}
              </label>

              <label className={styles.issueOption}>
                <input
                  type="radio"
                  name="payment-issue"
                  value="refused"
                  checked={issue === "refused"}
                  onChange={() => {
                    setIssue("refused");
                    setStatus("idle");
                  }}
                />
                {t("booking.payment.refused", "Paiement refusé")}
              </label>
            </fieldset>

            {status === "refused" && (
              <AlertBox
                type="error"
                title={t("booking.payment.refusedTitle", "Paiement refusé")}
                message={t(
                  "booking.payment.refusedMessage",
                  "La transaction simulée a été refusée. Votre dossier reste disponible : vous pouvez réessayer."
                )}
              />
            )}

            <div className={styles.actions}>
              <Button
                variant="cta"
                size="medium"
                label={
                  isProcessing
                    ? t("booking.payment.processing", "Traitement en cours…")
                    : amountLabel
                    ? t("booking.payment.pay", {
                        amount: amountLabel,
                        defaultValue: `Payer ${amountLabel}`,
                      })
                    : t("booking.payment.payPlain", "Payer")
                }
                disabled={isProcessing}
                onClick={handlePay}
              />
              <Button
                variant="ghost"
                size="medium"
                iconName="iconArrowLeft"
                iconPosition="left"
                label={t("booking.back", "Revenir au voyage")}
                disabled={isProcessing}
                onClick={() => navigate(`/trips/${tripPublicationId}/${tripDate}`)}
              />
            </div>
          </ContentCard>

          <ContentCard
            title={t("booking.payment.recap", "Votre dossier")}
            titleId="payment-recap"
            className={styles.card}
          >
            <dl className={styles.recap}>
              {booking?.title && (
                <div>
                  <dt>{t("booking.payment.trip", "Voyage")}</dt>
                  <dd>{booking.title}</dd>
                </div>
              )}
              {travelers.length > 0 && (
                <div>
                  <dt>{t("booking.payment.travelers", "Voyageurs")}</dt>
                  <dd>{travelers.length}</dd>
                </div>
              )}
              {(booking?.extras ?? []).map((extra) => (
                <div key={extra.label}>
                  <dt>{extra.label}</dt>
                  <dd>
                    {Number(extra.amount?.value ?? 0).toLocaleString(currentLocale())}{" "}
                    {extra.amount?.currency ?? ""}
                  </dd>
                </div>
              ))}
              {amountLabel && (
                <div className={styles.recapTotal}>
                  <dt>{t("booking.payment.total", "Total")}</dt>
                  <dd>{amountLabel}</dd>
                </div>
              )}
            </dl>
          </ContentCard>
        </div>
      </div>
    </div>
  );
}

export default Payments;
